import { useMemo, useState } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Search, Plus } from "lucide-react"
import { EmptyState, ErrorState } from "@/components/shared"
import { ResourceCard, ResourceCardSkeleton } from "@/components/fleet/ResourceCard"
import { useCalendar, useMaintenanceDue, useVehicles } from "@/lib/api/hooks"
import { AddVehicleDialog } from "./AddVehicleDialog"
import { buildEffectiveStatusMap, getEffectiveResourceStatus } from "./effectiveResourceStatus"
import { cn } from "@/lib/utils"
import type { Vehicle, ResourceStatus } from "@/types/fleet"

const statusFilters: { value: ResourceStatus | undefined; label: string }[] = [
  { value: undefined, label: "Alle" },
  { value: "available", label: "Verfügbar" },
  { value: "in_use", label: "In Benutzung" },
  { value: "maintenance", label: "Wartung" },
]

function isMaintenanceReminderVisible(dueDate: string) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return new Date(`${dueDate}T00:00:00`) <= today
}

interface VehiclesListProps {
  onReserve: (id: string, type: "vehicle" | "tool") => void
}

export function VehiclesList({ onReserve }: VehiclesListProps) {
  const [selectedStatus, setSelectedStatus] = useState<ResourceStatus | undefined>()
  const [searchQuery, setSearchQuery] = useState("")
  const [addVehicleOpen, setAddVehicleOpen] = useState(false)

  const {
    data: vehicles,
    isLoading,
    error,
    refetch,
  } = useVehicles()

  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  const todayEnd = new Date(todayStart)
  todayEnd.setHours(23, 59, 59, 999)

  const { data: todayCalendar } = useCalendar({
    start_date: todayStart.toISOString(),
    end_date: todayEnd.toISOString(),
    resource_type: "vehicle",
  })

  const { data: openMaintenance = [] } = useMaintenanceDue({ status: "open" })
  const maintenanceByAsset = useMemo(() => {
    const records = new Map<string, { count: number; hasOverdue: boolean }>()

    openMaintenance
      .filter((due) => due.resource_type === "vehicle" && isMaintenanceReminderVisible(due.due_date))
      .forEach((due) => {
        const current = records.get(due.asset_id) ?? { count: 0, hasOverdue: false }
        records.set(due.asset_id, {
          count: current.count + 1,
          hasOverdue: current.hasOverdue || due.severity === "overdue",
        })
      })

    return records
  }, [openMaintenance])

  const effectiveStatusMap = buildEffectiveStatusMap(todayCalendar?.resources, new Date())

  const vehiclesWithEffectiveStatus = vehicles?.map((vehicle: Vehicle) => ({
    ...vehicle,
    status: getEffectiveResourceStatus(vehicle.status, effectiveStatusMap, "vehicle", vehicle.id),
  }))

  const filteredVehicles = vehiclesWithEffectiveStatus?.filter((v: Vehicle) => {
    const matchesStatus = !selectedStatus || v.status === selectedStatus

    return matchesStatus && (
      v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.license_plate?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.location?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Fahrzeug suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10 bg-card border-border"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {statusFilters.map((filter) => (
            <button
              key={filter.label}
              onClick={() => setSelectedStatus(filter.value)}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
                selectedStatus === filter.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <ResourceCardSkeleton count={4} />
        </div>
      ) : error ? (
        <ErrorState
          message="Fahrzeuge konnten nicht geladen werden"
          onRetry={() => refetch()}
        />
      ) : !filteredVehicles || filteredVehicles.length === 0 ? (
        <EmptyState
          icon={Search}
          title={searchQuery ? "Keine Ergebnisse" : "Keine Fahrzeuge"}
          description={
            searchQuery
              ? "Keine Fahrzeuge entsprechen Ihrer Suche."
              : "Fügen Sie Ihr erstes Fahrzeug hinzu."
          }
          action={
            !searchQuery && (
              <Button className="gap-2 h-10 shadow-sm" onClick={() => setAddVehicleOpen(true)}>
                <Plus className="h-4 w-4" />
                Fahrzeug hinzufügen
              </Button>
            )
          }
        />
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {filteredVehicles.map((vehicle: Vehicle) => (
            <ResourceCard
              key={vehicle.id}
              resource={vehicle}
              type="vehicle"
              onReserve={onReserve}
              maintenanceDueCount={maintenanceByAsset.get(vehicle.id)?.count ?? 0}
              hasOverdueMaintenance={maintenanceByAsset.get(vehicle.id)?.hasOverdue ?? false}
            />
          ))}
        </div>
      )}
      <AddVehicleDialog open={addVehicleOpen} onOpenChange={setAddVehicleOpen} />
    </div>
  )
}
